import { ProductOption, ProductVariant } from 'lib/shopify/types';

export type Combination = {
  id: string;
  availableForSale: boolean;
  [key: string]: string | boolean;
};

/**
 * Helper function to check if a combination matches given option parameters
 * @param combination - The variant combination to check
 * @param optionParams - The selected option parameters to match against
 * @param validOptionNames - List of valid option names to consider
 * @returns boolean indicating if the combination matches and is available
 */
function doesCombinationMatch(
  combination: Combination,
  optionParams: Record<string, string>,
  validOptionNames: string[]
): boolean {
  // Filter to only consider valid option entries
  const relevantEntries = Object.entries(optionParams).filter(([key]) =>
    validOptionNames.includes(key)
  );

  // Check if all relevant options match and the combination is available
  return (
    relevantEntries.every(([key, value]) => combination[key] === value) &&
    combination.availableForSale
  );
}

/**
 * Creates a normalized map of variant combinations for efficient availability checking
 * Each combination includes the variant ID, availability, and flattened options
 */
export function createVariantCombinations(variants: ProductVariant[]): Combination[] {
  return variants.map((variant) => ({
    id: variant.id,
    availableForSale: variant.availableForSale,
    // Convert variant options to a flat key-value structure
    ...variant.selectedOptions.reduce(
      (accumulator, option) => ({
        ...accumulator,
        [option.name.toLowerCase()]: option.value
      }),
      {}
    )
  }));
}

/**
 * Checks if a specific variant option combination is available for sale
 * @param optionName - The name of the option being checked
 * @param optionValue - The value to check for the option
 * @param state - Current state of selected options
 * @param options - Available product options
 * @param combinations - Pre-computed variant combinations
 */
export function isVariantAvailable(
  optionName: string,
  optionValue: string,
  state: Record<string, string>,
  options: ProductOption[],
  combinations: Combination[]
): boolean {
  // Merge current state with the option being checked
  const optionParams = { ...state, [optionName]: optionValue };

  // Get lowercase option names for consistent comparison
  const validOptionNames = options.map((opt) => opt.name.toLowerCase());

  // Check if any combination matches the selected options
  return combinations.some((combination) =>
    doesCombinationMatch(combination, optionParams, validOptionNames)
  );
}

/**
 * Finds the first available option value from a list of possible values
 * Takes into account current selection state and valid combinations
 */
export function getFirstAvailableOptionValue(
  optionName: string,
  optionValues: string[],
  combinations: Combination[],
  currentState: Record<string, string>
): string | null {
  // Create array of valid option names, excluding 'image' and the current option
  const validOptionNames = [
    optionName,
    ...Object.keys(currentState).filter((key) => key !== 'image' && key !== optionName)
  ];

  // Find first value that creates a valid combination with current state
  for (const value of optionValues) {
    const optionParams = { ...currentState, [optionName]: value };

    if (
      combinations.some((combination) =>
        doesCombinationMatch(combination, optionParams, validOptionNames)
      )
    ) {
      return value;
    }
  }

  return null;
}

/**
 * Retrieves the image URL for a specific variant option value
 * Used primarily for color/visual variant displays
 */
export function getVariantImageForOption(
  variants: ProductVariant[],
  optionName: string,
  optionValue: string
): string | null {
  // Find a variant that has this option value
  const matchingVariant = variants.find((variant) =>
    variant.selectedOptions.some(
      (option) => option.name.toLowerCase() === optionName && option.value === optionValue
    )
  );

  return matchingVariant?.image?.url || null;
}

/**
 * Maps communication option values to their corresponding icon paths
 * Supports values containing 'bluetooth' or 'hotspot' (case insensitive)
 */
export function getAppCommunicationIcon(value: string): string | null {
  // Convert to lowercase for case-insensitive comparison
  const normalizedValue = value.toLowerCase();

  // Check if value includes bluetooth (prioritize bluetooth if both are present)
  if (normalizedValue.includes('bluetooth')) {
    return '/icons/bluetooth-icon.png';
  }
  
  // Check if value includes hotspot
  if (normalizedValue.includes('hotspot')) {
    return '/icons/hotspot-icon.png';
  }

  return null;
}

/**
 * Determines if the special silicone color option component should be used
 * based on option name and values
 */
export function isSiliconeColorOption(option: { name: string; values: string[] }): boolean {
  const colorNames = ['blue', 'red', 'pink', 'orange', 'black', 'teal'];
  const optionNameLower = option.name.toLowerCase();

  // Check if this is a color option
  if (optionNameLower !== 'color') return false;

  // Check if the values match typical silicone case colors
  const colorMatches = option.values.filter((value) => colorNames.includes(value.toLowerCase()));

  // If most values match our expected colors, assume it's a silicone case
  return colorMatches.length >= 3;
}
